/**
 * Google Cloud TTS does not reliably honour newlines as sentence boundaries. A
 * multi-line block whose lines lack terminal punctuation is treated as one
 * run-on "sentence" until the next full stop, producing pause-less audio that
 * rushes bulleted lists and can cut the final line short. This was first seen
 * with CJK (Chinese/Japanese/Korean) voices, but the same happens for Latin
 * (e.g. English) narration when lines carry no end char.
 *
 * There is no API parameter to control this (`AudioConfig` has no segmentation
 * field) and the affected languages use Chirp 3 HD voices, which reject SSML
 * entirely — so we cannot use `<s>`/`<p>` tags either. The reliable, voice-model
 * agnostic fix is to give the engine the punctuation it segments on: append a
 * script-appropriate sentence terminator to each line that lacks one. Punctuation
 * is not spoken, it only shapes prosody, so this is safe.
 *
 * Applied to all Google voices (the terminator is chosen per line from the line's
 * own script, so mixed-language blocks stay correct) and run before the JiT cache
 * key is hashed so keys change naturally and old run-on cache entries are orphaned.
 */

// Last meaningful char already implies a stop/pause — don't append (avoids `,。`).
// Latin: . ! ? ; :  — CJK/fullwidth: 。 ！ ？ ； ： … 、 ， plus ASCII comma.
const ALREADY_TERMINATED = /[.!?;:。！？；：…、，,]$/;

// Any CJK codepoint on the line ⇒ use the ideographic full stop.
// Kana, CJK ideographs (+ ext A), compatibility ideographs, Hangul, CJK punctuation.
const CJK_CHAR = /[぀-ヿ㐀-䶿一-鿿豈-﫿가-힯　-〿]/;

const terminatorForLine = (line: string): string =>
  CJK_CHAR.test(line) ? '。' : '.';

/**
 * Append a script-appropriate sentence terminator to each line of `text` that
 * does not already end in terminal/pausing punctuation.
 *
 * No-op unless the provider is Google. Also a no-op for single-line text (a lone
 * sentence needs no help, even with a trailing newline) — the run-on problem only
 * occurs across multiple lines. Blank lines are preserved untouched.
 */
export default function normalizeGoogleNarration(
  text: string,
  provider: string
): string {
  if (provider !== 'google' || !text) {
    return text;
  }

  const lines = text.split('\n');
  const contentLineCount = lines.filter(line => line.trim()).length;
  if (contentLineCount <= 1) {
    return text;
  }

  return lines
    .map(line => {
      const trimmed = line.replace(/[ \t]+$/, '');
      if (!trimmed) {
        return line;
      }
      if (ALREADY_TERMINATED.test(trimmed)) {
        return trimmed;
      }
      return trimmed + terminatorForLine(trimmed);
    })
    .join('\n');
}
